import { XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

type SessionCancellationNoticeProps = {
	session: Pick<
		Sessions.Session,
		| 'status'
		| 'status_description'
		| 'cancelled_at'
		| 'cancelled_by'
		| 'cancel_reason'
		| 'cancel_credit_restored'
	>;
	/** Quem está vendo: muda o texto de quem cancelou e do crédito. */
	viewer: 'user' | 'professional' | 'admin';
	/** Versão curta (uma linha) para cards e listas. */
	compact?: boolean;
	className?: string;
};

export const getSessionCancelledByLabel = (
	cancelledBy: Sessions.Session['cancelled_by'] | undefined,
	viewer: SessionCancellationNoticeProps['viewer'],
): string => {
	switch (cancelledBy) {
		case 'USER':
			return viewer === 'user' ? 'por você' : 'pelo paciente';
		case 'PROFESSIONAL':
			return viewer === 'professional' ? 'por você' : 'pelo profissional';
		case 'ADMIN':
			return 'pela equipe Plumaa';
		case 'SYSTEM':
			return 'automaticamente';
		default:
			return '';
	}
};

const formatCancelledAt = (value?: string | null): string | null => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString(
		'pt-BR',
		{ hour: '2-digit', minute: '2-digit' },
	)}`;
};

/**
 * Bloco com motivo, autor e data do cancelamento. Renderiza nada se a sessão
 * não estiver cancelada. Sessões canceladas antes desta funcionalidade caem
 * no fallback com `status_description`.
 */
export const SessionCancellationNotice = ({
	session,
	viewer,
	compact = false,
	className,
}: SessionCancellationNoticeProps) => {
	if (session.status !== 'CANCELLED') return null;

	const who = getSessionCancelledByLabel(session.cancelled_by, viewer);
	const when = formatCancelledAt(session.cancelled_at);
	const reason = session.cancel_reason?.trim();
	const headline = `Cancelada${who ? ` ${who}` : ''}${
		when ? ` em ${when}` : ''
	}`;

	const creditLine =
		session.cancel_credit_restored === true
			? viewer === 'user'
				? 'Seu crédito foi devolvido.'
				: 'O crédito do paciente foi devolvido.'
			: session.cancel_credit_restored === false
			  ? viewer === 'user'
					? 'Cancelamento com menos de 24h: o crédito não foi devolvido.'
					: 'Cancelamento com menos de 24h: o crédito do paciente não foi devolvido.'
			  : null;

	if (compact) {
		return (
			<p
				className={cn(
					'flex items-start gap-1.5 text-xs leading-snug text-red-700',
					className,
				)}
				data-testid="session-cancellation-notice"
			>
				<XCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
				<span className="min-w-0">
					<span className="font-semibold">{headline}.</span>{' '}
					{reason ? (
						<span className="text-red-800/80">
							Motivo: “{reason}”
						</span>
					) : session.status_description ? (
						<span className="text-red-800/80">
							{session.status_description}
						</span>
					) : null}
				</span>
			</p>
		);
	}

	return (
		<div
			className={cn(
				'rounded-xl border border-red-100 bg-red-50/80 px-3 py-2.5 text-xs leading-relaxed text-red-800',
				className,
			)}
			data-testid="session-cancellation-notice"
		>
			<p className="flex items-center gap-1.5 font-semibold">
				<XCircle size={15} className="shrink-0" aria-hidden />
				{headline}
			</p>
			{reason ? (
				<p className="mt-1.5">
					<span className="font-semibold">Motivo informado:</span>{' '}
					<span className="italic">“{reason}”</span>
				</p>
			) : session.status_description ? (
				<p className="mt-1.5">{session.status_description}</p>
			) : null}
			{creditLine ? (
				<p className="mt-1 text-[11px] opacity-90">{creditLine}</p>
			) : null}
		</div>
	);
};
